import { DocumentArrowUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { type Accept, type FileRejection, useDropzone } from 'react-dropzone';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { Button } from '../Button';

interface DropFieldProps {
    accept?: Accept;
    maxFileSize?: number;
    isMultiple?: boolean;
    disabled?: boolean;
    helperText?: string;
    errorMessage?: string;
    helperTextId?: string;
    errorMessageId?: string;
    onFilesAdded: (files: File[]) => void;
    onFilesRejected?: (rejections: FileRejection[]) => void;
}

const DropField = ({
    accept,
    maxFileSize,
    isMultiple = false,
    disabled = false,
    helperText,
    errorMessage,
    helperTextId,
    errorMessageId,
    onFilesAdded,
    onFilesRejected,
}: DropFieldProps) => {
    const { t } = useTranslation();

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        accept,
        maxSize: maxFileSize,
        multiple: isMultiple,
        disabled,
        noClick: true,
        noKeyboard: true,
        onDropAccepted: onFilesAdded,
        onDropRejected: onFilesRejected,
    });

    const hasError = Boolean(errorMessage);
    const isInteractive = !disabled && !isDragActive;

    const describedBy = [
        helperText ? helperTextId : undefined,
        hasError ? errorMessageId : undefined,
    ]
        .filter(Boolean)
        .join(' ') || undefined;

    return (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
            <div
                {...getRootProps({
                    tabIndex: -1,
                    className: cn(
                        'uploader-drop-area',
                        'tw-flex tw-flex-col tw-items-center tw-justify-center',
                        'tw-w-full tw-h-80 lg:tw-h-64 tw-p-6 tw-gap-5',
                        'tw-overflow-hidden tw-rounded-lg',
                        'tw-bg-fill-primary',
                        'tw-border tw-border-dashed tw-border-primary',
                        isInteractive && !hasError && 'hover:!tw-bg-[var(--gray-100)] hover:!tw-border-primary-hover',
                        isInteractive && hasError && 'hover:!tw-bg-[var(--gray-100)]',
                        isDragActive && !disabled && 'tw-border-solid tw-border-primary-active tw-bg-fill-tertiary',
                        hasError && !disabled && !isDragActive && 'tw-border-solid tw-border-error',
                        disabled && 'tw-cursor-not-allowed',
                    ),
                })}
                aria-disabled={disabled || undefined}
                aria-invalid={hasError || undefined}
                aria-describedby={describedBy}
                data-testid="uploader-drop-area"
            >
                <input {...getInputProps({ 'aria-label': t('UPLOADER.BROWSE') })} />

                <DocumentArrowUpIcon
                    className={cn(
                        'tw-w-12 tw-h-12 tw-shrink-0',
                        disabled ? 'tw-icon-inactive' : 'tw-icon-primary',
                    )}
                    aria-hidden="true"
                />

                <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                    <div
                        className={`!tw-text-t-sm lg:!tw-text-t-base tw-text-center ${disabled ? 'tw-text-t-inactive' : 'tw-text-t-primary'}`}
                    >
                        {t('UPLOADER.DRAG_AND_DROP')}
                    </div>
                    <Button
                        variant="outline"
                        hasIcon
                        icon={MagnifyingGlassIcon}
                        disabled={disabled}
                        onClick={open}
                        data-testid="uploader-browse-button"
                    >
                        {t('UPLOADER.BROWSE')}
                    </Button>
                </div>
            </div>

            <div className="tw-flex tw-flex-col tw-w-full">
                {helperText && (
                    <div
                        id={helperTextId}
                        className="tw-text-t-xs lg:tw-text-t-sm tw-text-t-secondary tw-w-full"
                    >
                        {helperText}
                    </div>
                )}
                <div className="tw-flex tw-items-center tw-w-full !tw-min-h-[16px] lg:!tw-min-h-[20px]">
                    {hasError && (
                        <div
                            id={errorMessageId}
                            role="alert"
                            className="tw-text-t-xs lg:tw-text-t-sm tw-text-error tw-w-full"
                        >
                            {errorMessage}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

DropField.displayName = 'DropField';

export { DropField };
